import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
  ScrollArea,
  Button,
  Badge,
  Separator,
  Label,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@machinemetrics/mm-react-components';
import { ArrowLeft, RotateCcw, UploadCloud, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import FanucScreen from '../../components/hmi/FanucScreen';
import { createActionBridge } from '../../components/hmi/core/HmiActionBridge';
import { dynamicHmiOperatorUrl } from '../../utils/machineRoutes';
import {
  listMachineTypes,
  getHmiDefByMachineTypeId,
  patchFieldHmi,
  patchMachineType,
} from '../../services/simDefApi';

// Only knobs that VISIBLY move the FANUC preview survive. The old no-op controls
// (zoneHint, role, widgetVariant, layoutPreset, density) are deleted — the runtime
// never read them. `shell` is gone too: the runtime is now one FANUC layout.
const SECTIONS = ['execution', 'cycle', 'program', 'feed', 'tlm', 'alarms', 'misc'];
const WIDGETS = ['preset', 'slider', 'toggle', 'enum', 'text', 'tlm_grid', 'readonly'];
const SPANS = ['1', '2', '3', '4'];
const HMI_THEMES = ['fanuc', 'siemens', 'brother', 'mazak', 'amada', 'cincinnati', 'citizen', 'generic'];

/** Mock live state so the preview shows a realistic running machine. */
const PREVIEW_STATE = {
  execution: 'ACTIVE',
  part_count: 12,
  target_qty: 40,
  feedrate: 90,
  override: 100,
  program: 'O1234',
  program_header: '(BRACKET OP10)',
  blockDelete: false,
  cycleMode: 'AUTO',
  cycle_progress: 63,
  faulted: false,
  alarm_code: null,
  alarm_message: null,
  TLM_T1_COUNT: 42,
  TLM_T2_COUNT: 88,
  TLM_T3_COUNT: 5,
  TLM_T4_COUNT: 100,
  TLM_T1_PRESET: 100,
  TLM_T2_PRESET: 100,
  TLM_T3_PRESET: 100,
  TLM_T4_PRESET: 100,
};

/** Merge a field's live hmiResolved with any unsaved local edit. */
function mergeField(field, edit) {
  if (!edit) return field;
  return { ...field, hmiResolved: { ...field.hmiResolved, ...edit } };
}

function FieldRow({ field, edited, selected, onSelect }) {
  const h = field.hmiResolved || {};
  return (
    <button
      type="button"
      onClick={() => onSelect(field.id)}
      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
        selected ? 'bg-accent ring-1 ring-primary' : 'hover:bg-muted'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm text-foreground truncate">{field.fieldKey}</span>
        {edited && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" title="Unsaved edit" />}
      </div>
      <div className="text-[11px] text-muted-foreground">
        {h.section || '?'} · {h.widget || '?'} · span {h.span ?? '?'}
      </div>
    </button>
  );
}

function PaletteGroup({ title, fields, editedIds, selectedId, onSelect, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!fields.length) return null;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {title}
        <Badge variant="secondary" className="ml-auto">{fields.length}</Badge>
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 pb-2">
          {fields.map((f) => (
            <FieldRow
              key={f.id}
              field={f}
              edited={editedIds.has(f.id)}
              selected={selectedId === f.id}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InspectorSelect({ label, value, options, onChange }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FieldInspector({ field, effective, onChange, onReset, isDirty }) {
  const h = effective.hmiResolved || {};
  const numeric = h.widget === 'slider';
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Selected field</div>
        <div className="font-mono text-sm text-foreground break-all">{field.fieldKey}</div>
        {field.label && <div className="text-xs text-muted-foreground">{field.label}</div>}
      </div>
      <Separator />
      <InspectorSelect
        label="Section (which soft-key page)"
        value={h.section || 'misc'}
        options={SECTIONS}
        onChange={(v) => onChange({ section: v })}
      />
      <InspectorSelect
        label="Widget (how it renders)"
        value={h.widget || 'readonly'}
        options={WIDGETS}
        onChange={(v) => onChange({ widget: v })}
      />
      <InspectorSelect
        label="Grid span (cell width)"
        value={String(h.span ?? 1)}
        options={SPANS}
        onChange={(v) => onChange({ span: Number(v) })}
      />
      {numeric && (
        <div className="grid grid-cols-3 gap-2">
          {['min', 'max', 'step'].map((k) => (
            <div key={k} className="flex flex-col gap-1.5">
              <Label className="text-xs text-muted-foreground">{k}</Label>
              <Input
                type="number"
                value={h[k] ?? ''}
                onChange={(e) =>
                  onChange({ [k]: e.target.value === '' ? undefined : Number(e.target.value) })
                }
              />
            </div>
          ))}
        </div>
      )}
      <Button variant="outline" size="sm" disabled={!isDirty} onClick={onReset}>
        <RotateCcw size={14} className="mr-1.5" />
        Revert this field
      </Button>
    </div>
  );
}

export default function HmiStudioPage() {
  const { machineTypeId } = useParams();
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [hmi, setHmi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [edits, setEdits] = useState({}); // fieldId -> partial hmiResolved override
  const [theme, setTheme] = useState('fanuc');
  const [themeDirty, setThemeDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState(null);
  const [machines, setMachines] = useState([]);
  const [liveMachineName, setLiveMachineName] = useState('');

  // A preview bridge — previewMode so edits stay in mock state, never hit REST.
  const mockRef = useRef(PREVIEW_STATE);
  const bridge = useMemo(
    () =>
      createActionBridge({
        previewMode: true,
        machineName: 'preview',
        setLiveState: (updater) => {
          mockRef.current = typeof updater === 'function' ? updater(mockRef.current) : updater;
        },
      }),
    []
  );

  useEffect(() => {
    listMachineTypes()
      .then((list) => setTypes(Array.isArray(list) ? list : []))
      .catch(() => setTypes([]));
    axios
      .get('/machinesim/api/machines')
      .then((res) => {
        const list = res.data?.machines;
        if (Array.isArray(list)) {
          setMachines(list);
          setLiveMachineName((prev) => prev || list[0]?.name || '');
        }
      })
      .catch(() => setMachines([]));
  }, []);

  const loadHmi = useCallback(async () => {
    if (!machineTypeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getHmiDefByMachineTypeId(machineTypeId);
      setHmi(data);
      setTheme(data.machineType?.hmiTheme || 'fanuc');
      setThemeDirty(false);
      setEdits({});
      setSelectedId((prev) => (data.fields?.some((f) => f.id === prev) ? prev : null));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
      setHmi(null);
    } finally {
      setLoading(false);
    }
  }, [machineTypeId]);

  useEffect(() => {
    loadHmi();
  }, [loadHmi]);

  // Field ids are UUID strings — key the Set by the raw id (NOT Number()).
  const editedIds = useMemo(() => new Set(Object.keys(edits)), [edits]);
  const dirtyCount = editedIds.size + (themeDirty ? 1 : 0);

  // Preview payload = server fields with unsaved edits merged in. This is what
  // makes every inspector change move the preview *before* Publish.
  const previewPayload = useMemo(() => {
    if (!hmi) return null;
    return {
      machineType: { ...hmi.machineType, hmiTheme: theme },
      fields: (hmi.fields || []).map((f) => mergeField(f, edits[f.id])),
    };
  }, [hmi, edits, theme]);

  const selectedField = useMemo(
    () => (hmi?.fields || []).find((f) => f.id === selectedId) || null,
    [hmi, selectedId]
  );
  const effectiveSelected = useMemo(
    () => (selectedField ? mergeField(selectedField, edits[selectedField.id]) : null),
    [selectedField, edits]
  );

  const updateSelected = useCallback(
    (patch) => {
      if (!selectedField) return;
      setEdits((prev) => {
        const cur = { ...(prev[selectedField.id] || {}) };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) delete cur[k];
          else cur[k] = v;
        }
        return { ...prev, [selectedField.id]: cur };
      });
      setPublishMsg(null);
    },
    [selectedField]
  );

  const revertSelected = useCallback(() => {
    if (!selectedField) return;
    setEdits((prev) => {
      const next = { ...prev };
      delete next[selectedField.id];
      return next;
    });
  }, [selectedField]);

  const publish = useCallback(async () => {
    if (!dirtyCount) return;
    setPublishing(true);
    setPublishMsg(null);
    try {
      await Promise.all(
        Object.entries(edits).map(([fieldId, body]) => patchFieldHmi(fieldId, body))
      );
      if (themeDirty) await patchMachineType(machineTypeId, { hmiTheme: theme });
      await loadHmi();
      setPublishMsg('Published.');
    } catch (e) {
      setPublishMsg(e.response?.data?.error || e.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  }, [dirtyCount, edits, themeDirty, theme, machineTypeId, loadHmi]);

  const coreFields = useMemo(
    () => (hmi?.fields || []).filter((f) => f.isCore),
    [hmi]
  );
  const otherFields = useMemo(
    () => (hmi?.fields || []).filter((f) => !f.isCore),
    [hmi]
  );

  const liveHmiUrl = useMemo(() => {
    if (!hmi?.machineType?.code || !liveMachineName) return null;
    return dynamicHmiOperatorUrl(liveMachineName, hmi.machineType.code);
  }, [hmi, liveMachineName]);

  if (!machineTypeId) return <p className="text-destructive p-6">Missing machine type id</p>;

  return (
    <div className="flex flex-col h-full min-h-[600px] bg-background text-foreground">
      {/* ── Top toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-card shrink-0 flex-wrap">
        <Link to="/stream/machine-types" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft size={18} />
        </Link>
        <span className="font-semibold">HMI Studio</span>
        <Select value={machineTypeId} onValueChange={(v) => navigate(`/stream/machine-types/${v}/hmi-studio`)}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Machine type" />
          </SelectTrigger>
          <SelectContent>
            {types.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.code} — {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="h-5 w-px bg-border" />

        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Brand theme</Label>
          <Select
            value={theme}
            onValueChange={(v) => {
              setTheme(v);
              setThemeDirty(v !== (hmi?.machineType?.hmiTheme || 'fanuc'));
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HMI_THEMES.map((th) => (
                <SelectItem key={th} value={th}>
                  {th}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {machines.length > 0 && (
            <Select value={liveMachineName} onValueChange={setLiveMachineName}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Live machine" />
              </SelectTrigger>
              <SelectContent>
                {machines.map((m) => (
                  <SelectItem key={m.name} value={m.name}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {liveHmiUrl && (
            <Button asChild variant="outline" size="sm">
              <a href={liveHmiUrl} target="_blank" rel="noopener noreferrer">
                Open live <ExternalLink size={14} className="ml-1.5" />
              </a>
            </Button>
          )}
          {publishMsg && <span className="text-xs text-muted-foreground">{publishMsg}</span>}
          <Button size="sm" disabled={!dirtyCount || publishing} onClick={publish}>
            <UploadCloud size={15} className="mr-1.5" />
            {publishing ? 'Publishing…' : `Publish${dirtyCount ? ` (${dirtyCount})` : ''}`}
          </Button>
        </div>
      </div>

      {error && <div className="px-4 py-2 text-sm text-destructive bg-card shrink-0">{error}</div>}

      {/* ── 3-region workspace ──────────────────────────────────────── */}
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
        {/* LEFT — field palette */}
        <ResizablePanel defaultSize={20} minSize={14} className="min-w-0">
          <div className="h-full flex flex-col bg-muted">
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border">
              Fields
            </div>
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2 flex flex-col gap-1">
                <PaletteGroup
                  title="Core"
                  fields={coreFields}
                  editedIds={editedIds}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  defaultOpen
                />
                <PaletteGroup
                  title="Others"
                  fields={otherFields}
                  editedIds={editedIds}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  defaultOpen={otherFields.length <= 12}
                />
                {!hmi?.fields?.length && !loading && (
                  <p className="text-xs text-muted-foreground p-2">No fields for this type.</p>
                )}
              </div>
            </ScrollArea>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* CENTER — live preview canvas (the runtime itself) */}
        <ResizablePanel defaultSize={54} minSize={30} className="min-w-0">
          <ScrollArea className="h-full bg-background">
            <div className="min-h-full flex flex-col items-center justify-center p-6 gap-3">
              {previewPayload ? (
                <>
                  <FanucScreen
                    machineType={previewPayload.machineType}
                    fields={previewPayload.fields}
                    values={PREVIEW_STATE}
                    bridge={bridge}
                    previewMode
                    designMode
                    selectedFieldId={selectedId}
                    onSelectField={setSelectedId}
                  />
                  <p className="text-xs text-muted-foreground">
                    Click any cell to edit it · soft-keys page the screen · mock state, not connected
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">{loading ? 'Loading…' : 'Select a machine type.'}</p>
              )}
            </div>
          </ScrollArea>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* RIGHT — inspector */}
        <ResizablePanel defaultSize={26} minSize={18} className="min-w-0">
          <div className="h-full flex flex-col">
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border">
              Inspector
            </div>
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-4">
                {effectiveSelected && selectedField ? (
                  <FieldInspector
                    field={selectedField}
                    effective={effectiveSelected}
                    isDirty={editedIds.has(selectedField.id)}
                    onChange={updateSelected}
                    onReset={revertSelected}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Select a field — in the list or by clicking a cell in the preview — to change how
                    it renders. Every change moves the preview; hit <strong>Publish</strong> to save.
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
